import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DescriptionIcon from '@mui/icons-material/Description';
import Button from '@mui/material/Button';

import { QCReviewItem, QCReviewSubControl } from '../../../interfaces/qcReviewData.interface';
import { Comment } from '../../../interfaces/assessment.interface';
import {
  QCAdminResponse,
  QCReview,
  QCVerdict,
  VerificationStatus,
  QC_ADMIN_RESPONSE_LABELS,
  QC_FLAGGED_VERDICTS,
  VERIFICATION_STATUSES,
  VERIFICATION_STATUS_LABELS,
} from '../../../interfaces/qcReview.interface';
import Comments from '../../Comments/Comments';
import CommentBuilder from '../../Comments/CommentBuilder';
import FileViewer from '../../FileViewer/FileViewer';
import { getSPDisposition, isRealSubControl, isRollupReview } from './qcAttention';

const DISPOSITION_LABELS: Record<string, string> = {
  implemented: 'Implemented',
  not_implemented: 'Not Implemented',
  not_applicable: 'Not Applicable',
  compensating_control: 'Compensating Control',
};

const VERDICT_CONFIG: Record<QCVerdict, { label: string; color: 'default' | 'error' | 'warning'; variant: 'filled' | 'outlined'; needsHuman: boolean }> = {
  agree: { label: 'Agree', color: 'default', variant: 'outlined', needsHuman: false },
  disagree: { label: 'Disagree', color: 'error', variant: 'filled', needsHuman: false },
  uncertain: { label: 'Uncertain — needs human', color: 'warning', variant: 'outlined', needsHuman: true },
  not_evaluated: { label: 'Not evaluated — needs human', color: 'warning', variant: 'outlined', needsHuman: true },
};

const ADMIN_RESPONSE_OPTIONS: QCAdminResponse[] = ['accept', 'edit', 'reject'];
const ADMIN_RESPONSE_COLORS: Record<QCAdminResponse, 'success' | 'inherit' | 'error'> = {
  accept: 'success',
  edit: 'inherit',
  reject: 'error',
};

// Per-QCReview accept/edit/reject control - the ADR-0005 agreement signal. Deliberately
// separate from the item-level "Your call on the assessor" disposition control below: this
// is the admin's reaction to ONE machine verdict, not to the assessor's answer (ADR-0007).
function AdminResponseControl({
  qcReview,
  canEdit,
  onSetAdminResponse,
}: {
  qcReview: QCReview;
  canEdit: boolean;
  onSetAdminResponse: (qcReviewId: number, response: QCAdminResponse) => Promise<void>;
}) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap' }}>
      <Typography variant="caption" color="textSecondary">Was this QC finding useful?</Typography>
      {ADMIN_RESPONSE_OPTIONS.map(option => (
        <Button
          key={option}
          size="small"
          variant={qcReview.admin_response === option ? 'contained' : 'outlined'}
          color={ADMIN_RESPONSE_COLORS[option]}
          disabled={!canEdit}
          onClick={() => onSetAdminResponse(qcReview.id, option)}
        >
          {QC_ADMIN_RESPONSE_LABELS[option]}
        </Button>
      ))}
    </Stack>
  );
}

interface SubControlRow {
  subControl: QCReviewSubControl;
  assessorDisposition?: string;
  spDisposition?: string;
  qcReview?: QCReview;
}

export interface ConsolidatedReviewViewProps {
  item: QCReviewItem;
  qcReviews: QCReview[];
  canEdit: boolean;
  onPostComment: (text: string, attachments: number[]) => Promise<void>;
  onSetVerificationStatus: (status: VerificationStatus) => Promise<void>;
  onSetAdminResponse: (qcReviewId: number, response: QCAdminResponse) => Promise<void>;
}

export default function ConsolidatedReviewView({
  item,
  qcReviews,
  canEdit,
  onPostComment,
  onSetVerificationStatus,
  onSetAdminResponse,
}: ConsolidatedReviewViewProps) {
  const [showComments, setShowComments] = useState(false);
  const [showDraftBuilder, setShowDraftBuilder] = useState(false);
  const [previewFile, setPreviewFile] = useState('');
  const [showFileViewer, setShowFileViewer] = useState(false);

  const { question } = item;

  const subControlRows: SubControlRow[] = useMemo(() => {
    return question.subControls
      .filter(isRealSubControl)
      .map(subControl => {
        const assessorQuestionAnswer = item.assessorQuestionAnswers.find(aqa => aqa.answerId === subControl.id);
        // qcReviews is already scoped to this AssessorAnswer (assessor_answer_id filtered
        // upstream); answer_id is the sub-control id, per qc/serializers.py.
        const qcReview = qcReviews.find(qc => qc.is_current && qc.answer_id === subControl.id);

        return {
          subControl,
          assessorDisposition: assessorQuestionAnswer?.compensatingOrNotApplicable || undefined,
          spDisposition: getSPDisposition(item, subControl.id),
          qcReview,
        };
      });
  }, [item, qcReviews]);

  // "Look here first" set (qc/constants.py: QC_FLAGGED_VERDICTS) — not_evaluated is
  // "needs human" too, but it isn't counted as a flagged finding.
  const flaggedRows = subControlRows.filter(row => row.qcReview && QC_FLAGGED_VERDICTS.includes(row.qcReview.verdict));

  // The Question-level roll-up verdict (CONTEXT.md: "Line-items vs aggregate call") - a
  // persisted QCReview in its own right, with its own rationale, previously discarded
  // entirely. Normally at most one is current per item, but render defensively as a list.
  const rollupReviews = useMemo(
    () => qcReviews.filter(qc => qc.is_current && isRollupReview(qc)),
    [qcReviews],
  );

  const aiDraft = useMemo(() => {
    if (flaggedRows.length === 0) return '';
    const lines = flaggedRows.map(row =>
      `• ${row.subControl.text}\n  ${row.qcReview!.rationale}`,
    );
    return `[AI-generated draft — edit before posting]\n\n${lines.join('\n\n')}`;
  }, [flaggedRows]);

  const handleOpenEvidence = (documentUrl: string) => {
    setPreviewFile(documentUrl);
    setShowFileViewer(true);
  };

  const handleVerificationChange = (event: SelectChangeEvent) => {
    onSetVerificationStatus(event.target.value as VerificationStatus);
  };

  const handleSaveDraft = async (text: string) => {
    setShowDraftBuilder(false);
    if (!text.trim()) return;
    await onPostComment(text, []);
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box>
            <Typography variant="h6">{question.title}</Typography>
            {question.description && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                {question.description}
              </Typography>
            )}
          </Box>
          {item.answer && <Chip label={item.answer} />}
        </Box>

        <Divider sx={{ my: 2 }} />

        {rollupReviews.length > 0 && (
          <>
            <Typography variant="subtitle2" gutterBottom>Question-Level Finding (Roll-up)</Typography>
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              {rollupReviews.map(qc => {
                const verdictConfig = VERDICT_CONFIG[qc.verdict];
                return (
                  <Box
                    key={qc.id}
                    sx={{
                      border: '1px solid',
                      borderColor: verdictConfig.color === 'error' ? 'error.main' : 'divider',
                      borderRadius: 1,
                      p: 1.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                      <Chip
                        size="small"
                        label={`QC: ${verdictConfig.label} (${Math.round((qc.confidence || 0) * 100)}%)`}
                        color={verdictConfig.color}
                        variant={verdictConfig.variant}
                      />
                      {qc.error_class && qc.error_class !== 'none' && (
                        <Chip size="small" label={qc.error_class.replace(/_/g, ' ')} variant="outlined" />
                      )}
                      {qc.suspected_injection && (
                        <Tooltip title="Suspected prompt injection in evidence — treat as needs-human, not a pass">
                          <Chip size="small" icon={<WarningAmberIcon />} label="Suspected injection" color="warning" />
                        </Tooltip>
                      )}
                    </Box>
                    <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                      {qc.rationale}
                    </Typography>
                    <AdminResponseControl qcReview={qc} canEdit={canEdit} onSetAdminResponse={onSetAdminResponse} />
                  </Box>
                );
              })}
            </Stack>
            <Divider sx={{ my: 2 }} />
          </>
        )}

        <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
          <Box sx={{ flex: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Assessor Finding</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {item.assessorFinding || '—'}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Flagged Sub-controls</Typography>
            <Typography variant="body2">
              {flaggedRows.length} of {subControlRows.length} sub-controls flagged for review
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>Sub-controls</Typography>
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {subControlRows.map(row => {
            const diverges = row.spDisposition && row.assessorDisposition && row.spDisposition !== row.assessorDisposition;
            const verdictConfig = row.qcReview ? VERDICT_CONFIG[row.qcReview.verdict] : undefined;

            return (
              <Box
                key={row.subControl.id}
                sx={{
                  border: '1px solid',
                  borderColor: verdictConfig?.color === 'error' ? 'error.main' : 'divider',
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {row.subControl.text}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: row.qcReview ? 1 : 0 }}>
                  <Typography variant="caption" color="textSecondary">
                    SP: {row.spDisposition ? DISPOSITION_LABELS[row.spDisposition] : 'Not Answered'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: diverges ? 'error.main' : 'text.secondary', fontWeight: diverges ? 'bold' : 'normal' }}
                  >
                    Assessor: {row.assessorDisposition ? DISPOSITION_LABELS[row.assessorDisposition] : 'Not Answered'}
                    {diverges ? ' (diverges from SP)' : ''}
                  </Typography>
                  {verdictConfig && (
                    <Chip
                      size="small"
                      label={`QC: ${verdictConfig.label} (${Math.round((row.qcReview!.confidence || 0) * 100)}%)`}
                      color={verdictConfig.color}
                      variant={verdictConfig.variant}
                    />
                  )}
                  {row.qcReview?.error_class && row.qcReview.error_class !== 'none' && (
                    <Chip size="small" label={row.qcReview.error_class.replace(/_/g, ' ')} variant="outlined" />
                  )}
                  {row.qcReview?.suspected_injection && (
                    <Tooltip title="Suspected prompt injection in evidence — treat as needs-human, not a pass">
                      <Chip size="small" icon={<WarningAmberIcon />} label="Suspected injection" color="warning" />
                    </Tooltip>
                  )}
                </Box>
                {row.qcReview && (
                  <>
                    <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                      {row.qcReview.rationale}
                    </Typography>
                    <AdminResponseControl qcReview={row.qcReview} canEdit={canEdit} onSetAdminResponse={onSetAdminResponse} />
                  </>
                )}
              </Box>
            );
          })}
        </Stack>

        <Divider sx={{ my: 2 }} />

        {item.evidence.length > 0 && (
          <>
            <Typography variant="subtitle2" gutterBottom>Evidence</Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
              {item.evidence.map(ev => (
                <Chip
                  key={ev.id}
                  icon={<DescriptionIcon />}
                  label={ev.document.split(/[#?]/)[0].split('/').pop()}
                  onClick={() => handleOpenEvidence(ev.document)}
                  variant="outlined"
                />
              ))}
            </Stack>
            <FileViewer
              previewFile={previewFile}
              canDownload
              showFileViewer={showFileViewer}
              closeFileViewer={() => setShowFileViewer(false)}
            />
          </>
        )}

        <Typography variant="subtitle2" gutterBottom>Comments</Typography>
        {showComments ? (
          <Comments
            disabled={!canEdit}
            comments={item.comments as unknown as Comment[]}
            handleClose={() => setShowComments(false)}
            postComment={onPostComment}
          />
        ) : (
          <Button size="small" onClick={() => setShowComments(true)}>Show comment thread</Button>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Tooltip title={flaggedRows.length === 0 ? 'No sub-controls were flagged for this question' : ''}>
            <span>
              <Button
                variant="outlined"
                disabled={!canEdit || flaggedRows.length === 0}
                onClick={() => setShowDraftBuilder(true)}
              >
                Edit AI Draft & Post Comment
              </Button>
            </span>
          </Tooltip>

          <Box>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id={`disposition-${question.id}`}>Your call on the assessor</InputLabel>
              <Select
                labelId={`disposition-${question.id}`}
                label="Your call on the assessor"
                value={item.verificationStatus || VERIFICATION_STATUSES.NOT_STARTED}
                onChange={handleVerificationChange}
                disabled={!canEdit}
              >
                {Object.values(VERIFICATION_STATUSES).map(status => (
                  <MenuItem key={status} value={status}>{VERIFICATION_STATUS_LABELS[status]}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, maxWidth: 260 }}>
              Was the assessor right? This is separate from responding to individual QC
              findings above.
            </Typography>
          </Box>
        </Box>

        <CommentBuilder
          canEdit={canEdit}
          commentBuilderDefault={aiDraft}
          showCommentBuilder={showDraftBuilder}
          handleHideCommentBuilder={() => setShowDraftBuilder(false)}
          handleSaveCommentBuilder={handleSaveDraft}
        />
      </CardContent>
    </Card>
  );
}
